import configs from '../config';
import { fetchWithTenant } from '../utils/apiClient';

const API_URL = `${configs.BACKEND_URL}/api`;

/**
 * System-level fetch (no tenant headers). Used for owner-only operations
 * that operate across tenants.
 */
function fetchSystem(url, options = {}) {
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    credentials: 'include',
  });
}

async function handleResponse(response) {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || `Request failed with status ${response.status}`);
  }
  return response.json();
}

/**
 * Tenant Management APIs
 */

// Get all tenants (Owner only — system-level, no tenant headers)
export async function getTenants() {
  const response = await fetchSystem(`${API_URL}/tenant`);
  return handleResponse(response);
}

// Get tenant by ID
export async function getTenantById(tenantId) {
  const response = await fetchWithTenant(`${API_URL}/tenant/${tenantId}`);
  return handleResponse(response);
}

// Create new tenant (Owner only — system-level)
export async function createTenant(tenantData) {
  const response = await fetchSystem(`${API_URL}/tenant`, {
    method: 'POST',
    body: JSON.stringify(tenantData),
  });
  return handleResponse(response);
}

// Regenerate admin invitation link for a tenant (Owner only — system-level)
export async function generateTenantInviteLink(tenantId) {
  const response = await fetchSystem(`${API_URL}/tenant/${tenantId}/regenerate-invite`, {
    method: 'POST',
  });
  return handleResponse(response);
}

// Update tenant (Owner only — system-level)
export async function updateTenant(tenantId, tenantData) {
  const response = await fetchSystem(`${API_URL}/tenant/${tenantId}`, {
    method: 'PUT',
    body: JSON.stringify(tenantData),
  });
  return handleResponse(response);
}

// Delete tenant (Owner only — system-level)
export async function deleteTenant(tenantId) {
  const response = await fetchSystem(`${API_URL}/tenant/${tenantId}`, {
    method: 'DELETE',
  });
  return handleResponse(response);
}

/**
 * Tenant Configuration APIs (tenant-scoped)
 */

export async function getTenantConfig() {
  const response = await fetchWithTenant(`${API_URL}/tenant/config`);
  return handleResponse(response);
}

export async function updateTenantConfig(tenantId, configData) {
  const response = await fetchWithTenant(`${API_URL}/tenant/config`, {
    method: 'PUT',
    body: JSON.stringify(configData),
  });
  return handleResponse(response);
}

export async function updateUpworkCredentials(tenantId, credentials) {
  const configData = {
    'upwork.apiKey': credentials.apiKey,
    'upwork.apiSecret': credentials.apiSecret || null,
    'upwork.apiToken': credentials.apiToken,
    'upwork.tenantId': credentials.tenantId,
    'upwork.isConfigured': !!(credentials.apiKey && credentials.apiToken && credentials.tenantId),
  };
  return updateTenantConfig(tenantId, configData);
}

// OpenAI is now managed via environment variables
export async function updateOpenAICredentials() {
  console.warn('OpenAI credentials are now managed via environment variables');
  return { success: true, message: 'OpenAI is configured via environment variables' };
}

// Gmail is now managed via environment variables
export async function updateGmailCredentials() {
  console.warn('Gmail credentials are now managed via environment variables');
  return { success: true, message: 'Gmail is configured via environment variables' };
}

export async function getJobFilterSuggestions() {
  const response = await fetchWithTenant(`${API_URL}/jobPosting/filter-suggestions`);
  return handleResponse(response);
}

export async function getProfileFilterSuggestions() {
  const response = await fetchWithTenant(`${API_URL}/tenant/filter-suggestions/from-profile`);
  return handleResponse(response);
}

export async function getProfileImportStatus() {
  const response = await fetchWithTenant(`${API_URL}/profile-import/status`);
  return handleResponse(response);
}

export async function getOnboardingActivationStatus() {
  const response = await fetchWithTenant(`${API_URL}/onboarding-activation/status`);
  return handleResponse(response);
}

export async function startOnboardingActivation(payload = {}) {
  const response = await fetchWithTenant(`${API_URL}/onboarding-activation/start`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function createProfileImportCapture(payload) {
  const response = await fetchWithTenant(`${API_URL}/profile-import/captures`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function applyProfileImportCapture(captureId) {
  const response = await fetchWithTenant(`${API_URL}/profile-import/captures/${captureId}/apply`, {
    method: 'POST',
  });
  return handleResponse(response);
}

export async function updateUpworkJobFilters(tenantId, filters) {
  const configData = {
    'jobFilters.criteria': filters,
    'jobFilters.lastUpdatedAt': new Date(),
  };
  return updateTenantConfig(tenantId, configData);
}

export async function updateMessageFilters(tenantId, filters) {
  const configData = {
    'messageFilters.enabled': filters.enabled,
    'messageFilters.criteria': filters.criteria,
    'messageFilters.lastUpdatedAt': new Date(),
  };
  return updateTenantConfig(tenantId, configData);
}

/**
 * Upwork OAuth 2.0 APIs
 */

export async function initiateUpworkOAuth(tenantId, redirectUri = null) {
  const params = redirectUri ? `?redirectUri=${encodeURIComponent(redirectUri)}` : '';
  const response = await fetchWithTenant(`${API_URL}/upwork/oauth/initiate${params}`);
  return handleResponse(response);
}

export async function getUpworkOAuthStatus() {
  const response = await fetchWithTenant(`${API_URL}/upwork/oauth/status`);
  return handleResponse(response);
}

export async function getUpworkUserSummary() {
  const response = await fetchWithTenant(`${API_URL}/upwork/oauth/summary`);
  return handleResponse(response);
}

export async function refreshUpworkToken() {
  const response = await fetchWithTenant(`${API_URL}/upwork/oauth/refresh`, {
    method: 'POST',
  });
  return handleResponse(response);
}

export async function disconnectUpwork(stepUpToken) {
  const headers = {};
  if (stepUpToken) {
    headers['X-Step-Up-Token'] = stepUpToken;
  }
  const response = await fetchWithTenant(`${API_URL}/upwork/oauth/disconnect`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
}

export async function updateUpworkOAuthCredentials(tenantId, credentials) {
  const response = await fetchWithTenant(`${API_URL}/tenant/upwork/oauth-credentials`, {
    method: 'PUT',
    body: JSON.stringify({
      clientId: credentials.clientId,
      clientSecret: credentials.clientSecret,
    }),
  });
  return handleResponse(response);
}

/**
 * Notification Channel APIs
 */

export async function updateNotificationConfig(configData) {
  return updateTenantConfig(null, configData);
}

export async function testNotificationChannel(channel) {
  const response = await fetchWithTenant(`${API_URL}/tenant/notifications/test/${channel}`, {
    method: 'POST',
  });
  return handleResponse(response);
}

/**
 * Sync APIs
 */

// Get sync status for all data types (jobs, proposals, rooms)
export async function getSyncStatus() {
  const response = await fetchWithTenant(`${API_URL}/tenant/sync-status`);
  return handleResponse(response);
}

// Trigger manual job sync
export async function syncJobsNow(options = {}) {
  const { immediate = true, maxPages = 100 } = options;
  const queryParams = new URLSearchParams({ immediate, maxPages });
  const response = await fetchWithTenant(`${API_URL}/tenant/sync/jobs?${queryParams}`, {
    method: 'POST',
  });
  return handleResponse(response);
}
